import * as React from 'react';
import { Form } from 'formik';

import { IEditForm } from '../types';

import {
  FormInput,
  Button,
  FormTextarea,
  FormCheckbox,
  FormSelect,
  FormObjectList,
  Icons,
} from 'src/components';
import ItemSelector from '../../components/ItemSelector';
import { withUser } from 'src/services/withUser';
import { isAdmin } from 'src/services/authorizeUser';

const groupOptions = [
  { value: 'GeneralInformation', label: 'General' },
  { value: 'ChannelSpecificInformation', label: 'Channel Specific' },
  { value: 'LinkSpecificInformation', label: 'Link Specific' },
  { value: 'AdSpecificInformation', label: 'Ad Specific' },
];
const fieldTypeOptions = [
  { value: 'Autocomplete', label: 'Autocomplete' },
  { value: 'DateBox', label: 'DateBox' },
  { value: 'DropDownList', label: 'DropDownList' },
  { value: 'RadioButton', label: 'RadioButton' },
  { value: 'TextBox', label: 'TextBox' },
];
const fieldsWithAddButton = ['DropDownList', 'RadioButton'];

const EditForm: React.FC<IEditForm> = ({
  id,
  parameterOptions,
  itemOptionsWithParameter,
  marketOptions,
  errors,
  isSubmitting,
  children,
  isValid,
  values,
  dirty,
  onRemove,
  onToggleArchive,
  isArchive,
  user,
}) => {
  const canHaveAdd =
    fieldsWithAddButton.indexOf(values[`type-${id}`]) !== -1 ||
    values[`parameterTypeConditions-${id}`].some(
      (el: any) => fieldsWithAddButton.indexOf(el.type) !== -1
    );

  return (
    <div className="pt-5 px-6 mx-px">
      <Form autoComplete="off">
        {children}
        <div className="flex">
          <div className="flex-1">
            <div className="flex mb-4">
              <label className="w-48 pt-2 pr-6" htmlFor={`name-${id}`}>
                Name*
              </label>
              <div className="flex-1">
                <FormInput className="flex-1 w-full" name={`name-${id}`} />
              </div>
            </div>
            <div className="flex mb-4">
              <label className="w-48 pt-2 pr-6" htmlFor={`orderRender-${id}`}>
                Order*
              </label>
              <div className="flex-1">
                <FormInput
                  className="w-full"
                  type="number"
                  name={`orderRender-${id}`}
                  step={1}
                  min={0}
                />
              </div>
            </div>
            <div className="flex items-center py-1 mb-4">
              <label className="w-48 pr-6" htmlFor={`mandatory-${id}`}>
                Mandatory
              </label>
              <FormCheckbox name={`mandatory-${id}`} />
            </div>
            {isAdmin(user.roles) && (
              <>
                <div className="flex mb-4">
                  <label
                    className="w-48 pr-6 pt-2"
                    htmlFor={`validation-${id}`}
                  >
                    Validation regex
                  </label>
                  <FormInput className="flex-1" name={`validation-${id}`} />
                </div>
                <div className="flex mb-4">
                  <label className="w-48 pr-6 pt-2" htmlFor={`msgError-${id}`}>
                    Validation error message
                  </label>
                  <FormInput className="flex-1" name={`msgError-${id}`} />
                </div>
              </>
            )}
          </div>
          <div className="flex-1 ml-8">
            <div className="flex mb-4">
              <span className="w-32 pt-2 pr-6">Group*</span>
              <div className="flex-1">
                <FormSelect
                  className="flex-1 w-full"
                  name={`parameterGroup-${id}`}
                  options={groupOptions}
                  initialValue={groupOptions[0]}
                />
              </div>
            </div>
            <div className="flex mb-4">
              <label className="w-32 pt-2 pr-6" htmlFor={`helpMessage-${id}`}>
                Help message
              </label>
              <div className="flex-1">
                <FormTextarea
                  className="w-full"
                  name={`helpMessage-${id}`}
                  rows={6}
                />
              </div>
            </div>
            <div className="flex mb-4 ml-32">
              <FormCheckbox name={`showHelpMessagePermanently-${id}`} />
              <label
                className="pr-6 pt-1"
                htmlFor={`showHelpMessagePermanently-${id}`}
              >
                Always visible
              </label>
            </div>
          </div>
        </div>

        <hr className="my-6 border-b border-grey-dark" />

        <div>
          <div className="flex mb-4">
            <span className="w-48 pt-2 pr-6">Type*</span>
            <div className="flex-1">
              <FormSelect name={`type-${id}`} options={fieldTypeOptions} />
            </div>
          </div>
          <div className="flex mb-4">
            <p className="w-48 pt-2 pr-6">Exceptions</p>
            <div className="flex-1">
              <FormObjectList
                name={`parameterTypeConditions-${id}`}
                addLabel="Add exception"
                addObject={{
                  conditionItem: {},
                  type: '',
                }}
              >
                {({ name, item }) => (
                  <>
                    <ItemSelector
                      name={`${name}.conditionItem`}
                      parameterOptions={parameterOptions}
                      itemOptionsWithParameter={itemOptionsWithParameter}
                      selectedItemId={
                        item.conditionItem && item.conditionItem.id
                      }
                      disabled={!itemOptionsWithParameter.length}
                    />
                    <FormSelect
                      className="w-48 mr-2"
                      name={`${name}.type`}
                      options={fieldTypeOptions}
                    />
                  </>
                )}
              </FormObjectList>
            </div>
          </div>
        </div>

        {canHaveAdd && (
          <>
            <hr className="my-6 border-b border-grey-dark" />
            <div>
              <div className="flex mb-4">
                <span className="pr-6 pt-2">
                  Created items will depend on the selected item of
                </span>
                <FormSelect
                  className="flex-1 mr-2"
                  name={`addedItemsConditionForItemInParameter-${id}`}
                  options={[
                    { label: 'None', value: null },
                    ...parameterOptions,
                  ]}
                />
              </div>

              <div className="flex items-center mb-4">
                <label className="w-48 pr-6" htmlFor={`defaultAdd-${id}`}>
                  Add-button
                </label>
                <FormCheckbox name={`defaultAdd-${id}`}>
                  Show by default
                </FormCheckbox>
              </div>

              <div className="flex mb-4">
                <p className="w-48 pt-2 pr-6">Market Exceptions</p>
                <div className="flex-1">
                  <FormObjectList
                    name={`parameterProfileAddConditions-${id}`}
                    addLabel="Add exception"
                    addObject={{
                      profile: marketOptions[0] && marketOptions[0].value,
                      visible: true,
                    }}
                  >
                    {({ name }) => (
                      <>
                        <FormSelect
                          className="flex-1"
                          name={`${name}.profile`}
                          options={marketOptions}
                        />
                        <FormCheckbox
                          className="w-32 ml-6"
                          name={`${name}.visible`}
                        >
                          Visible
                        </FormCheckbox>
                      </>
                    )}
                  </FormObjectList>
                </div>
              </div>
              <div className="flex mb-4">
                <p className="w-48 pt-2 pr-6">Item Exceptions</p>
                <div className="flex-1">
                  <FormObjectList
                    name={`parameterAddConditions-${id}`}
                    addLabel="Add exception"
                    addObject={{
                      item: {},
                      visible: true,
                    }}
                  >
                    {({ name, item }) => (
                      <>
                        <ItemSelector
                          name={`${name}.item`}
                          parameterOptions={parameterOptions}
                          itemOptionsWithParameter={itemOptionsWithParameter}
                          selectedItemId={item.item && item.item.id}
                          disabled={!itemOptionsWithParameter.length}
                        />
                        <FormCheckbox
                          className="w-32 ml-6"
                          name={`${name}.visible`}
                        >
                          Visible
                        </FormCheckbox>
                      </>
                    )}
                  </FormObjectList>
                </div>
              </div>
            </div>
          </>
        )}

        <hr className="my-6 border-b border-grey-dark" />

        <div>
          <div className="flex mb-4">
            <label className="w-48 pr-6" htmlFor={`defaultProfiles-${id}`}>
              Visibility Market
            </label>
            <FormCheckbox name={`defaultProfiles-${id}`}>
              Show by default in all Market
            </FormCheckbox>
          </div>
          <div className="flex mb-4">
            <p className="w-48 pt-2 pr-6">Exceptions</p>
            <div className="flex-1">
              <FormObjectList
                name={`parameterProfileVisibilityConditions-${id}`}
                addLabel="Add exception"
                addObject={{
                  profile: marketOptions[0] && marketOptions[0].value,
                  visible: true,
                }}
              >
                {({ name }) => (
                  <>
                    <FormSelect
                      className="flex-1"
                      name={`${name}.profile`}
                      options={marketOptions}
                    />
                    <FormCheckbox
                      className="w-32 ml-6"
                      name={`${name}.visible`}
                    >
                      Visible
                    </FormCheckbox>
                  </>
                )}
              </FormObjectList>
            </div>
          </div>
        </div>

        <hr className="my-6 border-b border-grey-dark" />

        <div>
          <div className="flex mb-4">
            <label className="w-48 pr-6" htmlFor={`defaultItems-${id}`}>
              Visibility Item
            </label>
            <FormCheckbox name={`defaultItems-${id}`}>
              Show by default
            </FormCheckbox>
          </div>
          <div className="flex mb-4">
            <p className="w-48 pt-2 pr-6">Exceptions</p>
            <div className="flex-1">
              <FormObjectList
                name={`parameterVisibilityConditions-${id}`}
                addLabel="Add exception"
                addObject={{
                  item: {},
                  visible: true,
                }}
              >
                {({ name, item }) => (
                  <>
                    <ItemSelector
                      name={`${name}.item`}
                      parameterOptions={parameterOptions}
                      itemOptionsWithParameter={itemOptionsWithParameter}
                      selectedItemId={item.item && item.item.id}
                      disabled={!itemOptionsWithParameter.length}
                    />
                    <FormCheckbox
                      className="w-32 ml-6"
                      name={`${name}.visible`}
                    >
                      Visible
                    </FormCheckbox>
                  </>
                )}
              </FormObjectList>
            </div>
          </div>
        </div>

        <div className="flex items-center pt-4 pb-5 bg-grey-transparent sticky pin-b pin-r">
          {onToggleArchive && (
            <Button
              onClick={onToggleArchive}
              type="button"
              size="small"
              background="grey"
              neutral={true}
            >
              {isArchive ? (
                <>
                  <Icons.Circle character="↺" className="mr-2" /> Restore
                </>
              ) : (
                <>
                  <Icons.Circle character="×" className="mr-2" /> Archive
                </>
              )}
            </Button>
          )}
          <div className="flex-1 text-yellow">
            {Object.values(errors).join(', ')}
          </div>
          <Button
            type="submit"
            background="grey"
            loading={isSubmitting}
            loadingLabel="Saving"
            disabled={!dirty || !isValid}
          >
            Save
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default withUser(EditForm);



// WEBPACK FOOTER //
// ./src/views/Admin/Parameters/components/EditForm.tsx